import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCheck } from 'lucide-react';
import { milkSessionsApi } from '../../services/resources';
import { getApiErrorMessage } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { useReferenceData } from '../../features/common/referenceData';
import { ExportExcel, type ExportColumn } from '../../features/common/ExportExcel';
import { ILMSCard } from '../../components/ILMSCard';
import { ILMSAlert } from '../../components/ILMSAlert';
import { ILMSButton } from '../../components/ILMSButton';
import { ILMSDatePicker } from '../../components/ILMSDatePicker';
import { ILMSStatusBadge } from '../../components/ILMSStatusBadge';
import { ILMSTableSkeleton } from '../../components/ILMSLoadingState';
import { cn } from '../../utils/cn';
import { formatDate, formatNumber, shortId, today } from '../../utils/format';
import type { MilkSession, MilkSessionType, MilkShift } from '../../types';

// ---------------------------------------------------------------------------
// Shift totals - every cow's milk in a shift added up (on request, once milk
// started being recorded cow by cow): approve a whole shift at once, and see
// whether it has gone out through a sale yet.
// ---------------------------------------------------------------------------

export function MilkShiftsPanel() {
  const { can } = useAuth();
  const canApprove = can('Milk', 'Approve');
  const queryClient = useQueryClient();
  const [showAll, setShowAll] = useState(false);
  const shiftsQuery = useQuery({ queryKey: ['milk-sessions', 'shifts'], queryFn: () => milkSessionsApi.shifts() });
  const approve = useMutation({
    mutationFn: (s: MilkShift) => milkSessionsApi.approveShift(s.production_date, s.session_type, 'Approved'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['milk-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['reference', 'milkShifts'] });
    },
  });

  const shifts = shiftsQuery.data ?? [];
  const visible = showAll ? shifts : shifts.slice(0, 6);

  return (
    <ILMSCard
      title="Shift totals"
      subtitle="Every cow's milk in a shift, added up. Sell a whole shift as one in Milk Sales (pick it under “Milk of shift”); approve all its entries here in one go."
      padded={false}
    >
      {shiftsQuery.isLoading ? (
        <div className="p-4"><ILMSTableSkeleton rows={3} columns={6} /></div>
      ) : shiftsQuery.isError ? (
        <div className="p-4"><ILMSAlert tone="critical">{getApiErrorMessage(shiftsQuery.error, 'Unable to load shift totals.')}</ILMSAlert></div>
      ) : shifts.length === 0 ? (
        <p className="p-4 text-secondary text-ink-500">No milk recorded in the last 14 days.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-table-text">
            <thead>
              <tr className="border-b border-ink-300/20 text-label text-ink-500">
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Shift</th>
                <th className="px-3 py-2 text-right">Cows</th>
                <th className="px-3 py-2 text-right">Litres</th>
                <th className="px-3 py-2 text-right">Fat %</th>
                <th className="px-3 py-2 text-right">SNF %</th>
                <th className="px-3 py-2 text-left">Approval</th>
                <th className="px-3 py-2 text-left">Sold via</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((s) => {
                const busy = approve.isPending && approve.variables === s;
                return (
                  <tr key={`${s.production_date}-${s.session_type}`} className="border-b border-ink-300/10 last:border-0">
                    <td className="px-3 py-1.5 whitespace-nowrap">{formatDate(s.production_date)}</td>
                    <td className="px-3 py-1.5">{s.session_type}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{s.animals}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-ink-900">{formatNumber(s.litres, 2)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{s.fat_percent ? formatNumber(s.fat_percent, 2) : '—'}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{s.snf_percent ? formatNumber(s.snf_percent, 2) : '—'}</td>
                    <td className="px-3 py-1.5">
                      {s.pending === 0 ? (
                        <ILMSStatusBadge status="Approved" size="sm" />
                      ) : (
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="text-helper-text text-warning-700">{s.pending} of {s.entries} pending</span>
                          {canApprove && (
                            <ILMSButton size="sm" variant="outline" icon={<CheckCheck />} loading={busy} disabled={approve.isPending} onClick={() => approve.mutate(s)}>
                              Approve all
                            </ILMSButton>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5">
                      {s.unsold_entries === 0
                        ? <span className="text-ink-700">{s.sale_codes.join(', ')}</span>
                        : s.sale_codes.length
                          ? <span className="text-helper-text text-warning-700">{s.sale_codes.join(', ')} · {formatNumber(s.unsold_litres, 2)} L not sold</span>
                          : <span className="text-helper-text text-warning-700">Pending sale</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {approve.isError && <div className="px-3 pb-3"><ILMSAlert tone="critical">{getApiErrorMessage(approve.error, 'Unable to approve this shift.')}</ILMSAlert></div>}
          {shifts.length > 6 && (
            <div className="px-3 py-2 border-t border-ink-300/10">
              <button type="button" className="text-helper-text text-brand-green-700 hover:underline" onClick={() => setShowAll((v) => !v)}>
                {showAll ? 'Show fewer' : `Show all ${shifts.length} shifts (last 14 days)`}
              </button>
            </div>
          )}
        </div>
      )}
    </ILMSCard>
  );
}

// ---------------------------------------------------------------------------
// Day sheet - Date | Animal | Morning (L, Fat, SNF) | Evening (L, Fat, SNF)
// | Day total, one row per cow per day (on request). Rejected entries are
// left out; a batch entry (before cow-by-cow recording) is its own row.
// ---------------------------------------------------------------------------

interface ShiftCell { litres: number; fat?: number; snf?: number; pending: boolean }
export interface DaySheetRow {
  key: string;
  date: string;
  animal: string;
  morning?: ShiftCell;
  evening?: ShiftCell;
  third?: ShiftCell;
  total: number;
  pending: boolean;
  isTotal?: boolean;
}

const SHIFT_FIELD: Record<MilkSessionType, 'morning' | 'evening' | 'third'> = { Morning: 'morning', Evening: 'evening', Third: 'third' };

function weightedAvg(pairs: [number, number][]): number | undefined {
  const w = pairs.reduce((n, [, l]) => n + l, 0);
  return w ? pairs.reduce((n, [v, l]) => n + v * l, 0) / w : undefined;
}

function mergeCells(entries: { litres: number; fat?: number; snf?: number; pending: boolean }[]): ShiftCell | undefined {
  if (!entries.length) return undefined;
  const litres = entries.reduce((n, e) => n + e.litres, 0);
  return {
    litres,
    fat: weightedAvg(entries.filter((e) => e.fat).map((e) => [e.fat!, e.litres])),
    snf: weightedAvg(entries.filter((e) => e.snf).map((e) => [e.snf!, e.litres])),
    pending: entries.some((e) => e.pending),
  };
}

/** Sessions -> one row per animal (or batch) per day, newest day first, each
 * day closed by a "Day total" row. */
export function buildDaySheet(sessions: MilkSession[], labelFor: (id: string) => string): DaySheetRow[] {
  const groups = new Map<string, { date: string; animal: string; cells: Record<string, { litres: number; fat?: number; snf?: number; pending: boolean }[]> }>();
  for (const s of sessions) {
    if (s.approval_status === 'Rejected') continue;
    const who = s.animal_id ? s.animal_id : `batch:${[...(s.group_animal_ids ?? [])].sort().join(',')}`;
    const animal = s.animal_id ? labelFor(s.animal_id) : `Batch: ${(s.group_animal_ids ?? []).map(labelFor).join(', ')}`;
    const key = `${s.production_date}|${who}`;
    const g = groups.get(key) ?? { date: s.production_date, animal, cells: {} };
    (g.cells[s.session_type] ??= []).push({
      litres: Number(s.litres), fat: s.fat_percent ? Number(s.fat_percent) : undefined,
      snf: s.snf_percent && Number(s.snf_percent) > 0 ? Number(s.snf_percent) : undefined, pending: s.approval_status === 'Pending',
    });
    groups.set(key, g);
  }

  const byDate = new Map<string, DaySheetRow[]>();
  for (const [key, g] of groups) {
    const row: DaySheetRow = { key, date: g.date, animal: g.animal, total: 0, pending: false };
    (Object.keys(SHIFT_FIELD) as MilkSessionType[]).forEach((shift) => {
      const cell = mergeCells(g.cells[shift] ?? []);
      if (cell) {
        row[SHIFT_FIELD[shift]] = cell;
        row.total += cell.litres;
        row.pending ||= cell.pending;
      }
    });
    (byDate.get(g.date) ?? byDate.set(g.date, []).get(g.date)!).push(row);
  }

  const out: DaySheetRow[] = [];
  for (const date of [...byDate.keys()].sort().reverse()) {
    const rows = byDate.get(date)!.sort((a, b) => a.animal.localeCompare(b.animal, undefined, { numeric: true }));
    out.push(...rows);
    const sum = (f: 'morning' | 'evening' | 'third') => {
      const cells = rows.map((r) => r[f]).filter(Boolean) as ShiftCell[];
      return cells.length ? mergeCells(cells) : undefined;
    };
    out.push({
      key: `${date}|total`, date, animal: `Day total · ${rows.length} ${rows.length === 1 ? 'row' : 'rows'}`, isTotal: true,
      morning: sum('morning'), evening: sum('evening'), third: sum('third'),
      total: rows.reduce((n, r) => n + r.total, 0), pending: rows.some((r) => r.pending),
    });
  }
  return out;
}

const fmt = (n: number | undefined, d = 2) => (n === undefined ? '—' : formatNumber(n, d));

function shiftDays(iso: string, days: number) {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

async function sessionsBetween(from?: string, to?: string): Promise<MilkSession[]> {
  const rows: MilkSession[] = [];
  for (let page = 1; page <= 50; page++) {
    const res = await milkSessionsApi.list({ page, page_size: 2000, date_from: from || undefined, date_to: to || undefined });
    rows.push(...res.items);
    if (rows.length >= res.total || res.items.length === 0) break;
  }
  return rows;
}

const EXPORT_COLUMNS: ExportColumn<DaySheetRow>[] = [
  { key: 'date', header: 'Date', render: (r) => formatDate(r.date) },
  { key: 'animal', header: 'Animal', render: (r) => r.animal },
  { key: 'm_l', header: 'Morning Litres', render: (r) => fmt(r.morning?.litres) },
  { key: 'm_fat', header: 'Morning Fat %', render: (r) => fmt(r.morning?.fat) },
  { key: 'm_snf', header: 'Morning SNF %', render: (r) => fmt(r.morning?.snf) },
  { key: 'e_l', header: 'Evening Litres', render: (r) => fmt(r.evening?.litres) },
  { key: 'e_fat', header: 'Evening Fat %', render: (r) => fmt(r.evening?.fat) },
  { key: 'e_snf', header: 'Evening SNF %', render: (r) => fmt(r.evening?.snf) },
  { key: 'third', header: 'Third Litres', render: (r) => fmt(r.third?.litres) },
  { key: 'total', header: 'Day Total Litres', render: (r) => fmt(r.total) },
  { key: 'status', header: 'Status', render: (r) => (r.pending ? 'Pending' : 'Approved') },
];

export function MilkDaySheet({ onRecord, canRecord }: { onRecord: () => void; canRecord: boolean }) {
  const [from, setFrom] = useState(() => shiftDays(today(), -6));
  const [to, setTo] = useState(today);
  const { data: ref } = useReferenceData(['animals']);
  const labelFor = React.useCallback((id: string) => ref.animals.find((a) => a.value === id)?.label ?? shortId(id), [ref.animals]);
  const sessionsQuery = useQuery({ queryKey: ['milk-sessions', 'day-sheet', from, to], queryFn: () => sessionsBetween(from, to) });
  const rows = useMemo(() => buildDaySheet(sessionsQuery.data ?? [], labelFor), [sessionsQuery.data, labelFor]);
  const hasThird = rows.some((r) => r.third);

  const cellGroup = (c: ShiftCell | undefined, total: boolean) => (
    <>
      <td className={cn('px-2 py-1 text-right tabular-nums border-l border-ink-300/15', total && 'font-semibold text-ink-900')}>{fmt(c?.litres)}</td>
      <td className="px-2 py-1 text-right tabular-nums">{fmt(c?.fat)}</td>
      <td className="px-2 py-1 text-right tabular-nums">{fmt(c?.snf)}</td>
    </>
  );

  return (
    <ILMSCard padded={false}>
      <div className="flex flex-wrap items-end justify-between gap-3 p-4 border-b border-ink-300/10">
        <div className="flex flex-wrap items-end gap-3">
          <ILMSDatePicker label="From" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
          <ILMSDatePicker label="To" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ExportExcel
            title="Milk Day Sheet" columns={EXPORT_COLUMNS} dateOf={(r) => r.date}
            fetchRows={async () => buildDaySheet(await sessionsBetween(), labelFor)}
          />
          {canRecord && <ILMSButton onClick={onRecord}>Record Session</ILMSButton>}
        </div>
      </div>
      {sessionsQuery.isLoading ? (
        <div className="p-4"><ILMSTableSkeleton rows={6} columns={6} /></div>
      ) : sessionsQuery.isError ? (
        <div className="p-4"><ILMSAlert tone="critical">{getApiErrorMessage(sessionsQuery.error, 'Unable to load milk entries.')}</ILMSAlert></div>
      ) : rows.length === 0 ? (
        <p className="p-4 text-secondary text-ink-500">No milk recorded between these dates.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse text-table-text text-ink-700">
            <thead>
              <tr className="text-label text-ink-500">
                <th rowSpan={2} className="px-3 py-1 text-left align-bottom">Date</th>
                <th rowSpan={2} className="px-3 py-1 text-left align-bottom">Animal</th>
                <th colSpan={3} className="px-2 py-1 text-center border-l border-ink-300/15">Morning</th>
                <th colSpan={3} className="px-2 py-1 text-center border-l border-ink-300/15">Evening</th>
                {hasThird && <th rowSpan={2} className="px-2 py-1 text-right align-bottom border-l border-ink-300/15">Third L</th>}
                <th rowSpan={2} className="px-3 py-1 text-right align-bottom border-l border-ink-300/15">Day total L</th>
                <th rowSpan={2} className="px-3 py-1 text-left align-bottom">Status</th>
              </tr>
              <tr className="border-b border-ink-300/20 text-label text-ink-500">
                {['Litres', 'Fat %', 'SNF %', 'Litres', 'Fat %', 'SNF %'].map((h, i) => (
                  <th key={i} className={cn('px-2 py-1 text-right', i % 3 === 0 && 'border-l border-ink-300/15')}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className={cn('border-b border-ink-300/10', r.isTotal && 'bg-brand-green-50/60 border-b-ink-300/30')}>
                  <td className="px-3 py-1 whitespace-nowrap">{r.isTotal ? '' : formatDate(r.date)}</td>
                  <td className={cn('px-3 py-1', r.isTotal ? 'font-semibold text-ink-900' : 'whitespace-normal break-words max-w-[22rem]')}>{r.animal}</td>
                  {cellGroup(r.morning, !!r.isTotal)}
                  {cellGroup(r.evening, !!r.isTotal)}
                  {hasThird && <td className="px-2 py-1 text-right tabular-nums border-l border-ink-300/15">{fmt(r.third?.litres)}</td>}
                  <td className={cn('px-3 py-1 text-right tabular-nums border-l border-ink-300/15', r.isTotal ? 'font-bold text-ink-900' : 'font-semibold')}>{fmt(r.total)}</td>
                  <td className="px-3 py-1">
                    {r.pending ? <ILMSStatusBadge status="Pending" size="sm" /> : r.isTotal ? '' : <ILMSStatusBadge status="Approved" size="sm" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="px-4 py-2 text-helper-text text-ink-500 border-t border-ink-300/10">
        Fat and SNF are litres-weighted averages. A cow with two entries in one shift (an entry and its correction) shows them added together — fix it under “All entries”.
      </p>
    </ILMSCard>
  );
}
